import type { TelegramChannelConfig, TelegramDestination, TelegramTopicConfig } from '@/lib/api'

export const EMPTY_TELEGRAM_DESTINATION = '__telegram_default__'

function valueToString(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

export function telegramTargetKey(chatId?: string | number | null, replyToMessageId?: string | number | null): string {
  return `${valueToString(chatId)}::${valueToString(replyToMessageId)}`
}

export function readTelegramTopicId(topic: TelegramTopicConfig | undefined): string {
  if (topic === null || topic === undefined) return ''
  if (typeof topic === 'object') {
    return valueToString(
      topic.reply_to_message_id ??
      topic.message_thread_id ??
      topic.topic_id ??
      topic.id
    )
  }
  return valueToString(topic)
}

function readTelegramTopicName(topicKey: string, topic: TelegramTopicConfig | undefined): string {
  if (topic && typeof topic === 'object') {
    const name = valueToString(topic.name ?? topic.label)
    if (name) return name
  }
  return topicKey.replace(/_/g, ' ')
}

export function normalizeTelegramDestinations(
  channels?: Record<string, TelegramChannelConfig>
): TelegramDestination[] {
  if (!channels) return []

  return Object.entries(channels).flatMap(([channelKey, channel]) => {
    const chatId = valueToString(channel.chat_id)
    if (!chatId) return []

    const channelName = valueToString(channel.name ?? channel.label) || channelKey.replace(/_/g, ' ')
    const destinations: TelegramDestination[] = [{
      id: channelKey,
      channel_key: channelKey,
      channel_name: channelName,
      topic_key: null,
      topic_name: null,
      label: channelName,
      chat_id: chatId,
      reply_to_message_id: null,
    }]

    Object.entries(channel.topics || {}).forEach(([topicKey, topic]) => {
      const topicName = readTelegramTopicName(topicKey, topic)
      destinations.push({
        id: `${channelKey}:${topicKey}`,
        channel_key: channelKey,
        channel_name: channelName,
        topic_key: topicKey,
        topic_name: topicName,
        label: `${channelName} / ${topicName}`,
        chat_id: chatId,
        reply_to_message_id: readTelegramTopicId(topic) || null,
      })
    })

    return destinations
  })
}

export function findTelegramDestination(
  destinations: TelegramDestination[],
  chatId?: string | number | null,
  replyToMessageId?: string | number | null
): TelegramDestination | undefined {
  const key = telegramTargetKey(chatId, replyToMessageId)
  return destinations.find((destination) =>
    telegramTargetKey(destination.chat_id, destination.reply_to_message_id) === key
  )
}
